import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { getUsers, getVehicleRepairs } from "../../../api/apiGetrequest";
import { AddRepairParts, UpdateVehicleRepair } from "../../../api/apirequest";
import TableHeader from "../../../Components/DataTable/TableHeader";
import { exportTableToExcel } from "../../../Components/Functions/ExcelDownLoader";
import { exportToPdf } from "../../../Components/Functions/PdfGenerator";
import { printContent } from "../../../Components/Functions/PrintPage";

import RepairCardsView from "../../MechanicApp/Maintenance";
import AddnewRepairRequest from "../AddRepair";
import SparePartsModal from "../SparesModal";

import { RepairActionModal } from "./RepairActionModal";
import { RepairListings } from "./RepairListings";
import { RepairTableStyles as styles } from "./RepairTableStyles";

const details = JSON.parse(localStorage.getItem("user"));

const RepairTable = () => {
  const tableRef = useRef(null);
  const [repairs, setRepairs] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedRepair, setSelectedRepair] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [spareParts, setSpareParts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newPO, setnewPO] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });
  const [updateForm, setUpdateForm] = useState({
    mechNotes: "",
    repairCost: 0,
    assignedTo: "",
    statusID: "",
    BookedDate: "",
    ReceivedDate: "",
    ReleaseDate: "",
    ReleaseNote: "",
    DateClosed: "",
  });

  // Export and print handlers
  const handleExport = () => {
    exportTableToExcel(tableRef, {
      fileName: "custom_report",
      sheetName: "Report",
      columnWidths: [15, 25, 10],
      addTimestamp: true,
    });
  };

  const exportToPdfHandler = async () => {
    if (!tableRef.current) {
      console.error("Table reference is null");
      alert("No table content available to export.");
      return;
    }
    if (isMobile) {
      alert("PDF export is only available in desktop view.");
      return;
    }

    const success = await exportToPdf(tableRef, {
      fileName: "Vehicle_Repairs",
      title: "Vehicle Repairs Report",
      subtitle: `Period: ${dateRange.startDate} to ${dateRange.endDate}`,
      margin: [15, 10],
      format: "a3",
      orientation: "landscape",
      scale: 2,
    });

    if (!success) {
      alert("Failed to generate PDF. Please try again.");
    }
  };

  const handlePrint = () => {
    if (!tableRef.current) {
      console.error("Table reference is null");
      alert("No table content available to print.");
      return;
    }
    if (isMobile) {
      alert("Printing is only available in desktop view.");
      return;
    }

    const success = printContent(tableRef, {
      title: "Vehicle Repairs Report",
      subtitle: `Period: ${dateRange.startDate} to ${dateRange.endDate}`,
    });

    if (!success) {
      alert("Failed to print. Please try again.");
    }
  };

  // Fetch repairs and mechanics on component mount and when date range changes
  useEffect(() => {
    fetchRepairs();
    fetchMechanics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  const fetchRepairs = async () => {
    try {
      const result = await getVehicleRepairs({
        vehicleID: 0,
        assignedTo: 0,
        startdate: dateRange.startDate,
        enddate: dateRange.endDate,
      });
      setRepairs(result);
    } catch (error) {
      toast.error("Failed to fetch repairs");
    }
  };

  const fetchMechanics = async () => {
    try {
      const result = await getUsers();
      setMechanics(result?.filter((m) => m.Role === "mechanic"));
    } catch (error) {
      toast.error("Failed to fetch mechanics");
    }
  };

  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateSubmit = async () => {
    setLoading(true);
    try {
      const result = await UpdateVehicleRepair({
        repairID: selectedRepair.RepairID,
        assignedTo: updateForm.assignedTo || selectedRepair.AssignedTo,
        mechNotes: updateForm.mechNotes,
        repairCost: updateForm.repairCost,
        statusID: updateForm.statusID || selectedRepair.StatusID,
        BookedDate: updateForm.BookedDate || selectedRepair.BookedDate,
        ReleaseDate: updateForm.ReleaseDate || selectedRepair.ReleaseDate,
        DateClosed: updateForm.DateClosed || selectedRepair.DateClosed,
        ReceivedDate: updateForm.ReceivedDate || selectedRepair.ReceivedDate,
        ReleaseNote: updateForm.ReleaseNote || selectedRepair.ReleaseNote,
      });

      if (result.success) {
        setShowModal(false);
        fetchRepairs();
        toast.success("Repair updated successfully");
        if (spareParts.length > 0) {
          try {
            const partsResult = await AddRepairParts({
              addedBy: details.data.userID,
              repairID: selectedRepair.RepairID,
              repairs: spareParts,
            });
            if (partsResult.success) {
              toast.success("Spare parts added to repair");
            } else {
              toast.error(partsResult.message || "Failed to add spare parts");
            }
          } catch (error) {
            console.error("Error adding spare parts:", error);
            toast.error("Failed to add spare parts");
          }
        }
      } else {
        toast.error(result.message || "Update failed");
      }
    } catch (error) {
      console.error("Error updating repair:", error);
      toast.error("Failed to update repair");
    }
    setLoading(false);
  };

  const handleRepairAction = (repair, action) => {
    setSelectedRepair(repair);

    switch (action) {
      case "assign":
        setUpdateForm({
          ...updateForm,
          statusID: 2,
          assignedTo: "",
        });
        break;
      case "review":
        setUpdateForm({
          ...updateForm,
          statusID: 3,
        });
        break;
      case "approve":
        setUpdateForm({
          ...updateForm,
          statusID: 4,
        });
        break;
      case "book":
        setUpdateForm({
          ...updateForm,
          statusID: 5,
        });
        break;
      case "receive":
        setUpdateForm({
          ...updateForm,
          statusID: 6,
          ReceivedDate: new Date(),
        });
        break;
      case "release":
        setUpdateForm({
          ...updateForm,
          statusID: 7,
          ReleaseDate: new Date(),
        });
        break;
      case "close":
        setUpdateForm({
          ...updateForm,
          statusID: 8,
          DateClosed: new Date(),
        });
        break;
      default:
        setUpdateForm({
          ...updateForm,
          statusID: repair.StatusID || "1",
          assignedTo: repair.AssignedTo || "",
        });
        break;
    }
    setShowModal(true);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleDeleteRepair = () => {};

  if (newPO) {
    return (
      <>
        <TableHeader
          title="Add Repair Request"
          showAddButton={false}
          buttonName={"Add Repair"}
          onBack={() => setnewPO(!newPO)}
          showBackButton={true}
          showPrintButton={false}
          showExcelButton={false}
          showPdfButton={false}
        />
        <AddnewRepairRequest
          setOpen={() => setnewPO(!newPO)}
          fetchRepairs={fetchRepairs}
        />
      </>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.dateRangeContainer}>
        <div style={styles.dateInputGroup}>
          <label style={styles.dateLabel}>
            Start Date:
            <input
              type="date"
              style={styles.dateInput}
              value={dateRange.startDate}
              onChange={(e) => handleDateChange("startDate", e.target.value)}
            />
          </label>
        </div>
        <div style={styles.dateInputGroup}>
          <label style={styles.dateLabel}>
            End Date:
            <input
              type="date"
              style={styles.dateInput}
              value={dateRange.endDate}
              onChange={(e) => handleDateChange("endDate", e.target.value)}
            />
          </label>
        </div>
      </div>
      <TableHeader
        title="Maintenance List"
        showAddButton={true}
        buttonName={"Add Repair"}
        onAdd={() => setnewPO(!newPO)}
        showBackButton={false}
        showPrintButton={true}
        showExcelButton={true}
        onExcelExport={handleExport}
        onPdfExport={exportToPdfHandler}
        onPrint={handlePrint}
        showPdfButton={true}
        onDeleteRepair={handleDeleteRepair}
      />
      <SparePartsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        spareParts={spareParts}
        repair={selectedRepair}
        fetchRepairs={fetchRepairs}
      />

      <div style={styles.responsiveView}>
        {!isMobile ? (
          <div style={styles.desktopView} ref={tableRef}>
            <RepairListings
              repairs={repairs}
              onRepairAction={handleRepairAction}
              onSparePartsView={(repair) => {
                setIsModalOpen(true);
                setSelectedRepair(repair);
              }}
              onDeleteRepair={handleDeleteRepair}
            />
          </div>
        ) : (
          <RepairCardsView
            repairs={repairs}
            mechanics={mechanics}
            fetchRepairs={fetchMechanics}
          />
        )}
      </div>

      {showModal && (
        <RepairActionModal
          showModal={showModal}
          selectedRepair={selectedRepair}
          mechanics={mechanics}
          updateForm={updateForm}
          setUpdateForm={setUpdateForm}
          loading={loading}
          spareParts={spareParts}
          setSpareParts={setSpareParts}
          onClose={() => {
            setShowModal(false);
            setSpareParts([]);
          }}
          onSubmit={handleUpdateSubmit}
        />
      )}
    </div>
  );
};

export default RepairTable;
